import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

export type FrontendErrorNotification = {
  detail?: string;
  status?: number | string;
  subtitle?: string;
  title?: string;
  userMessage: string;
};

@Injectable({ providedIn: 'root' })
export class FrontendErrorNotifierService {
  private lastKey = '';
  private lastAt = 0;

  showError(notification: FrontendErrorNotification): void {
    if (this.isNonBlockingDomReconciliationError(notification)) {
      return;
    }

    const key = `${notification.status || ''}|${notification.userMessage}|${notification.detail || ''}`;
    const now = Date.now();

    if (key === this.lastKey && now - this.lastAt < 2500) {
      return;
    }

    this.lastKey = key;
    this.lastAt = now;

    if (Swal.isVisible()) {
      return;
    }

    const detail = notification.detail || 'Sin detalle técnico disponible.';
    const status = notification.status ? `Código: ${notification.status}` : '';

    Swal.fire({
      icon: 'error',
      title: notification.title || 'No fue posible completar la operación',
      html: `
        <div style="text-align:left">
          <p style="margin:0 0 10px;color:#1f2a44;font-weight:700;">
            ${this.escapeHtml(notification.userMessage)}
          </p>
          ${notification.subtitle ? `<p style="margin:0 0 10px;color:#667085;">${this.escapeHtml(notification.subtitle)}</p>` : ''}
          <div style="background:#f6f8fc;border:1px solid #dbe3f1;border-radius:10px;padding:10px;margin-top:10px;">
            <div style="font-size:11px;font-weight:800;color:#0b2b63;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px;">
              Detalle técnico ${status ? `· ${this.escapeHtml(status)}` : ''}
            </div>
            <pre style="white-space:pre-wrap;word-break:break-word;margin:0;max-height:150px;overflow:auto;font-size:11px;color:#33405c;">${this.escapeHtml(detail)}</pre>
          </div>
        </div>
      `,
      confirmButtonText: 'Entendido',
      denyButtonText: 'Copiar detalle',
      showDenyButton: true,
      confirmButtonColor: '#0b2b63',
      denyButtonColor: '#6c757d',
      heightAuto: false,
    }).then((result) => {
      if (result.isDenied) {
        void this.copyToClipboard(detail);
      }
    });
  }

  private async copyToClipboard(value: string): Promise<void> {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private isNonBlockingDomReconciliationError(notification: FrontendErrorNotification): boolean {
    const text = [
      notification.title,
      notification.userMessage,
      notification.subtitle,
      notification.detail,
    ].filter(Boolean).join('\n').toLowerCase();

    return text.includes('insertbefore') && (
      text.includes('not a child of this node') ||
      text.includes('the node before which the new node is to be inserted') ||
      text.includes('notfounderror')
    );
  }
}
